// Deterministic mock data for hidden-tile previews; never fetched.
import {NotificationItemData} from 'platform/components';

import {groupBy, prop, take, zipWith} from 'ramda';

import {
  DashboardBusinessCaseResponseBody,
  DashboardInterestResponseBody,
  DashboardVehicleResponseBody,
  SaleFunnelResponseBody,
  TaskResponseBody,
  VehicleStockOverviewResponseBody,
} from '@dms/api/core';

import {parseDate} from 'shared/web';

import {WORKSHOP_OVERVIEW_TILES} from '../components/WorkshopOverview/constants/workshopOverviewTiles';
import {
  WorkshopOverviewSectionState,
  WorkshopSection,
} from '../components/WorkshopOverview/types/WorkshopOverviewTileConfig';

const PREVIEW_VEHICLE_BASE: Omit<DashboardVehicleResponseBody, 'id' | 'title'> = {
  imageUrl: null,
  manufacturedOn: '2022-03-01',
  mileage: '45000',
  fuelType: null,
  transmission: null,
  price: {amount: '450000', currency: 'CZK'},
  priceWithVat: {amount: '450000', currency: 'CZK'},
  priceWithoutVat: {amount: '371900', currency: 'CZK'},
  isVatDeductible: true,
  isArchived: false,
  isReserved: false,
  saleType: null,
  buyingState: null,
  sellingState: null,
  isAdvertised: true,
  isPhotographed: true,
  tags: [],
};

const latestVehicles: DashboardVehicleResponseBody[] = [
  {...PREVIEW_VEHICLE_BASE, id: 'preview-vehicle-1', title: 'Škoda Octavia'},
  {...PREVIEW_VEHICLE_BASE, id: 'preview-vehicle-2', title: 'Volkswagen Golf'},
  {...PREVIEW_VEHICLE_BASE, id: 'preview-vehicle-3', title: 'Hyundai i30'},
  {...PREVIEW_VEHICLE_BASE, id: 'preview-vehicle-4', title: 'Toyota Corolla'},
];

const vehicleStockOverview: VehicleStockOverviewResponseBody = {
  totalForSale: 477,
  selling: 323,
  buying: 61,
  motExpirationWarning: 12,
  motExpiredWarning: 106,
  motWarningPeriod: {
    expiredThrough: '2026-09-30',
    expiringFrom: '2026-09-30',
    expiringThrough: '2026-10-30',
  },
};

const PREVIEW_WORKSHOP_COUNTS: Record<WorkshopSection, number[]> = {
  critical: [3, 7],
  warning: [2, 4],
  today: [1, 5],
};
const PREVIEW_WORKSHOP_SECTION_ORDER: WorkshopSection[] = ['critical', 'warning', 'today'];
const PREVIEW_WORKSHOP_TILES_BY_SECTION = groupBy((tile) => tile.section, WORKSHOP_OVERVIEW_TILES);

const workshopSections: WorkshopOverviewSectionState[] = PREVIEW_WORKSHOP_SECTION_ORDER.map(
  (section) => ({
    section,
    tiles: zipWith(
      (tile, count: number) => ({tile, count}),
      take(2, prop(section, PREVIEW_WORKSHOP_TILES_BY_SECTION) ?? []),
      prop(section, PREVIEW_WORKSHOP_COUNTS)
    ),
  })
);

const PREVIEW_BUSINESS_CASE_BASE: Omit<
  DashboardBusinessCaseResponseBody,
  'id' | 'customer' | 'vehicle'
> = {
  price: {amount: '450000', currency: 'CZK'},
  type: null,
  state: 'CONCEPT',
  tradeType: null,
  tags: [],
  assignees: [],
  owner: null,
};

const latestBusinessCases: DashboardBusinessCaseResponseBody[] = [
  {
    ...PREVIEW_BUSINESS_CASE_BASE,
    id: 'preview-business-case-1',
    customer: 'Jan Novák',
    vehicle: 'Škoda Octavia',
  },
  {
    ...PREVIEW_BUSINESS_CASE_BASE,
    id: 'preview-business-case-2',
    customer: 'Petra Svobodová',
    vehicle: 'Volkswagen Golf',
  },
  {
    ...PREVIEW_BUSINESS_CASE_BASE,
    id: 'preview-business-case-3',
    customer: 'Tomáš Dvořák',
    vehicle: 'Hyundai i30',
  },
  {
    ...PREVIEW_BUSINESS_CASE_BASE,
    id: 'preview-business-case-4',
    customer: 'Lucie Králová',
    vehicle: 'Toyota Corolla',
  },
];

const PREVIEW_INTEREST_BASE: Omit<DashboardInterestResponseBody, 'id' | 'customer' | 'vehicle'> = {
  type: 'BUYING',
  state: 'NEW',
  source: null,
  tags: [],
  assignees: [],
  owner: null,
};

const latestInterests: DashboardInterestResponseBody[] = [
  {
    ...PREVIEW_INTEREST_BASE,
    id: 'preview-interest-1',
    customer: 'Jan Novák',
    vehicle: 'Škoda Octavia',
  },
  {
    ...PREVIEW_INTEREST_BASE,
    id: 'preview-interest-2',
    customer: 'Petra Svobodová',
    vehicle: 'Volkswagen Golf',
  },
  {
    ...PREVIEW_INTEREST_BASE,
    id: 'preview-interest-3',
    customer: 'Tomáš Dvořák',
    vehicle: 'Hyundai i30',
  },
  {
    ...PREVIEW_INTEREST_BASE,
    id: 'preview-interest-4',
    customer: 'Lucie Králová',
    vehicle: 'Toyota Corolla',
  },
];

const saleFunnel: SaleFunnelResponseBody = {
  newInterests: 24,
  activeInterests: 18,
  offers: 11,
  contracts: 6,
  sold: 4,
  closed: 9,
};

const PREVIEW_TASK_BASE: Omit<
  TaskResponseBody,
  'id' | 'title' | 'type' | 'priority' | 'state' | 'dueTo'
> = {
  createdAt: '2026-09-27T09:00:00Z',
  createdBy: 'preview-user',
  reportedBy: 'preview-user',
  assignedTo: 'preview-user',
  dueDate: null,
  dueFrom: null,
  finishedAt: null,
  relatedRecordId: null,
  description: null,
};

const myTasks: TaskResponseBody[] = [
  {
    ...PREVIEW_TASK_BASE,
    id: 'preview-task-1',
    title: 'Call customer about test drive',
    type: 'CALL',
    priority: 'HIGH',
    state: 'TODO',
    dueTo: '2026-10-01T10:00:00Z',
  },
  {
    ...PREVIEW_TASK_BASE,
    id: 'preview-task-2',
    title: 'Prepare offer for Škoda Octavia',
    type: 'TASK',
    priority: 'MEDIUM',
    state: 'IN_PROGRESS',
    dueTo: '2026-10-02T14:00:00Z',
  },
  {
    ...PREVIEW_TASK_BASE,
    id: 'preview-task-3',
    title: 'Meeting with fleet manager',
    type: 'MEETING',
    priority: 'LOW',
    state: 'TODO',
    dueTo: '2026-10-03T09:00:00Z',
  },
  {
    ...PREVIEW_TASK_BASE,
    id: 'preview-task-4',
    title: 'Follow up on financing approval',
    type: 'TASK',
    priority: 'HIGH',
    state: 'TODO',
    dueTo: '2026-10-03T15:00:00Z',
  },
];

const notifications: NotificationItemData[] = [
  {
    id: 'preview-notification-1',
    icon: 'social/notifications',
    userName: 'Jan Novák',
    created: parseDate('2026-09-27T09:00:00Z'),
    title: 'It has been 45 days since pricing of the vehicle',
    message: 'Škoda Kodiaq',
    isRead: false,
  },
  {
    id: 'preview-notification-2',
    icon: 'social/notifications',
    userName: 'Petra Svobodová',
    created: parseDate('2026-09-26T14:00:00Z'),
    title: 'A new interest has been assigned to you',
    message: 'Volkswagen Golf',
    isRead: false,
  },
  {
    id: 'preview-notification-3',
    icon: 'social/notifications',
    userName: 'Jan Novák',
    created: parseDate('2026-09-25T10:00:00Z'),
    title: 'The business case has been updated',
    message: 'Škoda Octavia',
    isRead: true,
  },
];

export const DASHBOARD_TILE_PREVIEW_FIXTURES = {
  latestVehicles,
  vehicleStockOverview,
  workshopSections,
  saleFunnel,
  latestBusinessCases,
  latestInterests,
  notifications,
  myTasks,
} as const;
